import { PlusCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import FloatingInput from '../FloatingInput.jsx';
import DatePicker from '../../Common/DatePicker.jsx';
import toast from 'react-hot-toast';

export function ProfessionalDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    onPrevious,
    isFirstTab,
    isLastTab,
    hideButtons = false,
    readOnly = false,
    onSaveTrigger,
}) {
    // Ensure experiences is always an array
    const formData = {
        ...employeeData,
        experiences: employeeData.experiences || []
    };
    
    // State for professional validation errors
    const [professionalErrors, setProfessionalErrors] = useState({
        fresherStatus: '',
        experiences: []
    });

    // Calculate experience duration between from and to dates (returns {years, months})
    const calculateExperienceDuration = useCallback((fromDate, toDate) => {
        if (!fromDate || !toDate) return null;
        try {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) return null;

            let years = to.getFullYear() - from.getFullYear();
            let months = to.getMonth() - from.getMonth();
            if (months < 0 || (months === 0 && to.getDate() < from.getDate())) {
                years--;
                months += 12;
            }
            if (to.getDate() < from.getDate()) months--;
            if (months < 0) {
                years--;
                months += 12;
            }
            return { years: Math.max(0, years), months: Math.max(0, months) };
        } catch (e) {
            return null;
        }
    }, []);

    // Calculate total experience across all entries (sum of durations)
    const calculateTotalExperience = useCallback((experiences) => {
        if (!experiences || experiences.length === 0) return null;
        let totalMonths = 0;
        for (const exp of experiences) {
            const fromDate = exp.fromDate || exp.from_date;
            const toDate = exp.toDate || exp.to_date;
            const dur = calculateExperienceDuration(fromDate, toDate);
            if (dur) totalMonths += dur.years * 12 + dur.months;
        }
        if (totalMonths === 0) return null;
        return {
            years: Math.floor(totalMonths / 12),
            months: totalMonths % 12,
        };
    }, [calculateExperienceDuration]);

    // Validation function to check if experience data is complete
    const validateExperience = (experience) => {
        const errors = {};
        if (!experience.company || experience.company.trim() === '') {
            errors.company = 'Company name is required';
        }
        if (!experience.position || experience.position.trim() === '') {
            errors.position = 'Position is required';
        }
        if (!experience.fromDate) {
            errors.fromDate = 'From date is required';
        }
        if (!experience.toDate) {
            errors.toDate = 'To date is required';
        }
        
        // Validate that To Date is not less than From Date
        if (experience.fromDate && experience.toDate) {
            const fromDate = new Date(experience.fromDate);
            const toDate = new Date(experience.toDate);
            
            if (toDate < fromDate) {
                errors.toDate = 'To Date cannot be earlier than From Date';
            }
        }
        
        return errors;
    };

    // Validation function to check overall professional validity
    const validateProfessional = () => {
        const errors = {
            fresherStatus: '',
            experiences: []
        };

        // If not fresher, must have at least one experience with complete information
        if (!formData.isFresher) {
            if (!formData.experiences || formData.experiences.length === 0) {
                errors.fresherStatus = 'Please either mark as fresher or add at least one experience';
            } else {
                // Validate each experience
                const experienceErrors = formData.experiences.map(exp => validateExperience(exp));
                errors.experiences = experienceErrors;
                
                // Check if at least one experience has complete information
                const hasValidExperience = experienceErrors.some(expError => 
                    Object.keys(expError).length === 0
                );
                
                if (!hasValidExperience) {
                    errors.fresherStatus = 'Please provide complete information for at least one experience';
                }
            }
        }

        setProfessionalErrors(errors);
        return Object.keys(errors.fresherStatus).length === 0 && 
               errors.experiences.every(expError => Object.keys(expError).length === 0);
    };

    const handleInputChange = useCallback((e) => {
        const { name, checked } = e.target;

        // Toggle flags like isFresher without clearing existing data so that
        // checking/unchecking does not wipe unsaved experience rows.
        // Data will only be cleared when Update is clicked (in handleSave)
        setEmployeeData(prev => ({ 
            ...prev, 
            [name]: checked,
            experiences: prev.experiences,
        }));
        
        // Clear fresher status error when checkbox is checked
        if (checked) {
            setProfessionalErrors(prev => ({
                ...prev,
                fresherStatus: ''
            }));
        } else {
            // Validate when unchecking fresher
            setTimeout(() => validateProfessional(), 100);
        }
    }, [setEmployeeData]);

    const handleNestedInputChange = useCallback((index, field, value) => {
        setEmployeeData(prev => ({
            ...prev,
            experiences: prev.experiences.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            ),
        }));
        
        // Clear specific field error when user enters valid data
        if (value && value.trim() !== '') {
            setProfessionalErrors(prev => ({
                ...prev,
                experiences: prev.experiences.map((expErrors, i) => {
                    if (i === index) {
                        const newExpErrors = { ...expErrors };
                        delete newExpErrors[field];
                        return newExpErrors;
                    }
                    return expErrors;
                })
            }));
            
            // Clear fresher status error when any experience field has data
            setProfessionalErrors(prev => ({
                ...prev,
                fresherStatus: ''
            }));
        } else {
            // Only validate the specific field that is empty
            setProfessionalErrors(prev => ({
                ...prev,
                experiences: prev.experiences.map((expErrors, i) => {
                    if (i === index) {
                        const newExpErrors = { ...expErrors };
                        if (field === 'company' && (!value || value.trim() === '')) {
                            newExpErrors.company = 'Company name is required';
                        } else if (field === 'position' && (!value || value.trim() === '')) {
                            newExpErrors.position = 'Position is required';
                        } else if (field === 'fromDate' && !value) {
                            newExpErrors.fromDate = 'From date is required';
                        } else if (field === 'toDate' && !value) {
                            newExpErrors.toDate = 'To date is required';
                        }
                        return newExpErrors;
                    }
                    return expErrors;
                })
            }));
        }
        
        // Special validation for date fields to check date range (use updated local values to avoid stale state)
        if (field === 'fromDate' || field === 'toDate') {
            const base = formData.experiences[index] || {};
            const updatedExperience = { ...base, [field]: value };
            if (updatedExperience.fromDate && updatedExperience.toDate) {
                const fromDate = new Date(updatedExperience.fromDate);
                const toDate = new Date(updatedExperience.toDate);
                if (toDate < fromDate) {
                    setProfessionalErrors(prev => ({
                        ...prev,
                        experiences: prev.experiences.map((expErrors, i) => {
                            if (i === index) {
                                return { ...expErrors, toDate: 'To Date cannot be earlier than From Date' };
                            }
                            return expErrors;
                        })
                    }));
                } else {
                        // Clear date range error if dates are valid
                    setProfessionalErrors(prev => ({
                        ...prev,
                        experiences: prev.experiences.map((expErrors, i) => {
                            if (i === index) {
                                const newExpErrors = { ...expErrors };
                                delete newExpErrors.toDate;
                                return newExpErrors;
                            }
                            return expErrors;
                        })
                    }));
                }
            }
        }
    }, [setEmployeeData, formData.experiences]);

    const handleAddItem = useCallback(() => {
        setEmployeeData(prev => ({
            ...prev,
            experiences: [
                ...(prev.experiences || []),
                { 
                    company: '', 
                    position: '', 
                    fromDate: '', 
                    toDate: '',
                    description: ''
                },
            ],
        }));
        
        // Add empty error object for new experience
        setProfessionalErrors(prev => ({
            ...prev,
            experiences: [...prev.experiences, {}]
        }));
    }, [setEmployeeData]);

    const handleRemoveItem = useCallback((index) => {
        setEmployeeData(prev => ({
            ...prev,
            experiences: prev.experiences.filter((_, i) => i !== index),
        }));
        
        // Remove corresponding errors
        setProfessionalErrors(prev => ({
            ...prev,
            experiences: prev.experiences.filter((_, i) => i !== index)
        }));
    }, [setEmployeeData]);

    const handleSave = async () => {
        // Validate professional requirements
        if (!validateProfessional()) {
            toast.error('Please complete the required professional details before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if professional validation fails
        }
        
        // Only send professional section fields
        // If isFresher is checked, clear experiences array when saving
        const sectionData = {
            isFresher: formData.isFresher || false,
            experiences: formData.isFresher 
                ? [] // Clear experiences only when saving with isFresher checked
                : (formData.experiences || []).map(exp => ({
                    company: exp.company || '',
                    position: exp.position || '',
                    fromDate: exp.fromDate || '',
                    toDate: exp.toDate || '',
                    description: exp.description || '',
                    user_id: employeeData.userId || employeeData.user?.id // Add user_id as required by backend
                })),
        };
        
        try {
            const result = await onSaveSection(sectionData);
            // If isFresher is checked and save was successful, clear experiences from state
            if (formData.isFresher && result) {
                setEmployeeData(prev => ({
                    ...prev,
                    experiences: []
                }));
            }
            // Only navigate to next tab if save is successful
            if (onNext && !isLastTab) {
                onNext();
            }
            toast.success('Professional details saved successfully', { position: 'top-right' });
            return { saved: true };
        } catch (error) {
            console.error('Save failed:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });
            return { saved: false };
        }
    };

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(handleSave);
        }
    }, [onSaveTrigger, handleSave]);

    const handleNext = async () => {
        await handleSave();
    };

    const handlePrevious = () => {
        if (onPrevious) {
            onPrevious();
        }
    };

    return (
        <div className="space-y-4">
            {/* Top Navigation Buttons */}
            {/* Keep in DOM but hidden when hideButtons is true */}
            <div className={`sticky top-0 z-5 flex justify-end mb-4 bg-white py-2 shadow-sm ${hideButtons ? 'hidden' : ''}`}>
                <div className="flex space-x-2">
                    {!isFirstTab && (
                        <button
                            onClick={onPrevious}
                            className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
                            disabled={loading}
                        >
                            Previous
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {!isLastTab ? (
                        <button
                            onClick={handleNext}
                            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
                            disabled={loading || readOnly}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            onClick={handleSave}
                            className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm"
                            disabled={loading || readOnly}
                        >
                            {employeeData.id ? 'Update' : 'Save'}
                        </button>
                    )}
                </div>
            </div>
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-75' : ''}>
            <div>
                <label className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        name="isFresher"
                        checked={formData.isFresher}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm font-medium text-gray-700">Fresher</span>
                    {/* Overall Experience card */}
                    {formData.experiences?.length > 0 && calculateTotalExperience(formData.experiences) !== null && (() => {
                        const total = calculateTotalExperience(formData.experiences);
                        return (
                            <div className="mt-4 p-3">
                                <div className="bg-blue-50 border border-blue-200 rounded-none px-3 py-2 inline-flex items-center space-x-2">
                                    <span className="text-blue-600 font-semibold text-sm">
                                        Overall Experience:
                                    </span>
                                    <span className="text-blue-800 font-bold text-base">
                                        {total.years}
                                    </span>
                                    <span className="text-blue-600 text-xs">
                                        {total.years === 1 ? 'year' : 'years'}
                                    </span>
                                    {total.months > 0 && (
                                        <>
                                            <span className="text-blue-800 font-bold text-base">
                                                {total.months}
                                            </span>
                                            <span className="text-blue-600 text-xs">
                                                {total.months === 1 ? 'month' : 'months'}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </label>
                {professionalErrors.fresherStatus && (
                    <div className="text-red-500 text-sm mt-1">{professionalErrors.fresherStatus}</div>
                )}
            </div>
            {!formData.isFresher && (
                <div>
                    {formData.experiences.map((experience, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                <FloatingInput
                                    id={`company-${index}`}
                                    label="Previous Company Name"
                                    value={experience.company}
                                    onChange={(e) => handleNestedInputChange(index, 'company', e.target.value)}
                                    required
                                    error={professionalErrors.experiences[index]?.company}
                                />
                                <FloatingInput
                                    id={`position-${index}`}
                                    label="Position"
                                    value={experience.position}
                                    onChange={(e) => handleNestedInputChange(index, 'position', e.target.value)}
                                    required
                                    error={professionalErrors.experiences[index]?.position}
                                />
                                <div>
                                    {(() => {
                                        const today = new Date().toISOString().slice(0,10);
                                        const candidates = [experience.toDate, employeeData.joiningDate, today].filter(Boolean);
                                        const fromMax = candidates.length ? candidates.sort()[0] : today;
                                        let fromMaxError = 'From Date cannot be in the future';
                                        if (experience.toDate && fromMax === experience.toDate) {
                                            fromMaxError = 'From Date cannot be after To Date';
                                        } else if (employeeData.joiningDate && fromMax === employeeData.joiningDate) {
                                            fromMaxError = 'From Date cannot be after Date of Join';
                                        }
                                        return (
                                            <DatePicker
                                                value={experience.fromDate || ''}
                                                onChange={(e) => handleNestedInputChange(index, 'fromDate', e.target.value)}
                                                label="From Date"
                                                required
                                                min={employeeData.user?.dateOfBirth}
                                                max={fromMax}
                                                minErrorText="From Date cannot be earlier than Date of Birth"
                                                maxErrorText={fromMaxError}
                                                validateOnType={true}
                                            />
                                        );
                                    })()}
                                    {professionalErrors.experiences[index]?.fromDate && (
                                        <div className="text-red-500 text-xs mt-1">{professionalErrors.experiences[index]?.fromDate}</div>
                                    )}
                                </div>
                                <div>
                                    {(() => {
                                        const today = new Date().toISOString().slice(0,10);
                                        const candidates = [employeeData.joiningDate, today].filter(Boolean);
                                        const toMax = candidates.length ? candidates.sort()[0] : today;
                                        const toMaxError = (employeeData.joiningDate && toMax === employeeData.joiningDate)
                                            ? 'To Date cannot be later than Date of Join'
                                            : 'To Date cannot be in the future';
                                        return (
                                            <DatePicker
                                                value={experience.toDate || ''}
                                                onChange={(e) => handleNestedInputChange(index, 'toDate', e.target.value)}
                                                label="To Date"
                                                required
                                                min={experience.fromDate}
                                                max={toMax}
                                                minErrorText="To Date cannot be earlier than From Date"
                                                maxErrorText={toMaxError}
                                                validateOnType={true}
                                            />
                                        );
                                    })()}
                                    {professionalErrors.experiences[index]?.toDate && (
                                        <div className="text-red-500 text-xs mt-1">{professionalErrors.experiences[index]?.toDate}</div>
                                    )}
                                </div>
                                <FloatingInput
                                    id={`description-${index}`}
                                    label="Description"
                                    value={experience.description}
                                    onChange={(e) => handleNestedInputChange(index, 'description', e.target.value)}
                                />
                            </div>
                            {/* Experience duration card - per company */}
                            {(() => {
                                const fromDate = experience.fromDate || experience.from_date;
                                const toDate = experience.toDate || experience.to_date;
                                const dur = fromDate && toDate ? calculateExperienceDuration(fromDate, toDate) : null;
                                if (!dur) return null;
                                return (
                                    <div className="flex items-center space-x-2 mt-2">
                                        <div className="bg-blue-50 border border-blue-200 rounded-none px-2 py-1.5 flex items-center space-x-2">
                                            <span className="text-blue-600 font-semibold text-sm">
                                                Experience:
                                            </span>
                                            <span className="text-blue-800 font-bold text-base">
                                                {dur.years}
                                            </span>
                                            <span className="text-blue-600 text-xs">
                                                {dur.years === 1 ? 'year' : 'years'}
                                            </span>
                                            {dur.months > 0 && (
                                                <>
                                                    <span className="text-blue-800 font-bold text-base">
                                                        {dur.months}
                                                    </span>
                                                    <span className="text-blue-600 text-xs">
                                                        {dur.months === 1 ? 'month' : 'months'}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                            <button
                                onClick={() => handleRemoveItem(index)}
                                className="mt-2 text-red-600 hover:text-red-800"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={handleAddItem}
                        className="m-2 text-blue-600 hover:text-blue-800"
                    >
                        <PlusCircle className="inline-block mr-1" size={16} /> Add Experience
                    </button>
                </div>
            )}
           {feedback && (
    <div className={`mt-2 ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
        {typeof feedback.message === 'string'
            ? feedback.message
            : feedback.message?.error || feedback.message?.message || 'An unknown error occurred.'}
    </div>
)}

            </fieldset>
        </div>
    );
}